import { AxiosResponse, AxiosStatic } from "axios";
import { IPatchPayload } from "../../components/course-author/helpers/patches";
import { IFullOrganization } from "../../parsers/resolve";
import { AxiosInterceptedResult } from "../interceptors";

export interface IOrganizationsApiDependencies {
  axios: AxiosStatic;
  saltcomplianceApiRoot: string;
}

export interface IOrganizationsApi {
  createNewOrganization: (
    accountId: string,
    activeOrg: string,
    newOrganization: IFullOrganization
  ) => Promise<AxiosResponse>;
  patchOrganization: (accountId: string, orgId: string, patchPayload: IPatchPayload) => Promise<AxiosResponse>;
  patchOrgImports: (
    accountId: string,
    orgId: string,
    file: File,
    type: "accounts" | "groups" | "members"
  ) => Promise<AxiosResponse>;
  getAdministrableBy: (accountId: string) => Promise<AxiosInterceptedResult<string[]>>;
  getUndeletedNonChildrenOf: (orgId: string, accountId: string) => Promise<AxiosInterceptedResult<string[]>>;
  getEditableByRelativeTo: (accountId: string, organizationId: string) => Promise<AxiosInterceptedResult<string[]>>;
}

export class OrganizationsApi implements IOrganizationsApi {
  private readonly apiRoot: string;
  private readonly axios: AxiosStatic;

  constructor(dependencies: IOrganizationsApiDependencies) {
    this.apiRoot = dependencies.saltcomplianceApiRoot;
    this.axios = dependencies.axios;
  }

  public createNewOrganization = (
    accountId: string,
    activeOrg: string,
    newOrganization: IFullOrganization
  ): Promise<AxiosResponse> => {
    return this.axios.post(`${this.apiRoot}/orgs`, newOrganization, {
      headers: {
        "Active-Org": activeOrg,
        "Active-User": accountId,
      },
    });
  };

  public patchOrganization = (
    accountId: string,
    orgId: string,
    patchPayload: IPatchPayload
  ): Promise<AxiosResponse> => {
    return this.axios.post(`${this.apiRoot}/orgs/${orgId}/_patches`, patchPayload, {
      params: { activeUser: accountId },
    });
  };

  public patchOrgImports = (accountId: string, orgId: string, file: File, type: string): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append(`${type}.csv`, file, `${type}.csv`);
    return this.axios.patch(`${this.apiRoot}/organizations/${orgId}/${type}`, formData, {
      params: { activeUser: accountId },
    });
  };

  public getAdministrableBy = (accountId: string) => {
    return this.axios.get(`${this.apiRoot}/collections/orgs/administrableBy/${accountId}`, {
      params: { activeUser: accountId },
    }) as Promise<AxiosInterceptedResult<string[]>>;
  };

  public getUndeletedNonChildrenOf = (orgId: string, accountId: string) => {
    return this.axios.get(`${this.apiRoot}/collections/orgs/undeletedNonChildrenOf/${orgId}`, {
      params: { activeUser: accountId },
    }) as Promise<AxiosInterceptedResult<string[]>>;
  };
  public getEditableByRelativeTo = (accountId: string, organizationId: string) => {
    return this.axios.get(`${this.apiRoot}/collections/orgs/editableBy/${accountId}/relativeToOrg/${organizationId}`, {
      params: { activeUser: accountId },
    }) as Promise<AxiosInterceptedResult<string[]>>;
  };
}
